import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';

import React, { useEffect, useState } from 'react';
import EditSpecies from './EditSpecies';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import IconButton from '@mui/material/IconButton';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import Typography from '@mui/material/Typography';
import Switch from '@mui/material/Switch';
import { SearchSpeciesOutput } from '../../peer-colab-model/PetKey/Main/Model/1_0_Initial/PathItems';
import { useToast } from '../../providers/ToastProvider';
import { Session } from '../../peer-colab-model/Session';
import { SpeciesManagement } from '../../peer-colab-model/PetKey/BackofficeFE/Client/PathItems';
import AISuggestions from '../../components/AISuggestions';

function SpeciesAndBreedsList() {
  const { onError } = useToast()
  const [search, setSearch] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [newSpeciesName, setNewSpeciesName] = useState('');
  const [editingSpecies, setEditingSpecies] = useState<string | null>(null);
  const [filteredSpecies, setFilteredSpecies] = useState<SearchSpeciesOutput[]>([]);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [speciesToDelete, setSpeciesToDelete] = useState<{ id: string, name: string } | null>(null);

  const searchSpecies = async (query: string) => {
    onError((await Session.getClient().request(SpeciesManagement.searchSpecies({ query, limit: 100, offset: 0 })))
      .maybeOk(setFilteredSpecies))
  }

  useEffect(() => {
    searchSpecies('');
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleAddSpecies = () => {
    setNewSpeciesName(''); // Reset input value before opening dialog
    setDialogOpen(true);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setNewSpeciesName('');
  };

  const handleDialogOk = async () => {
    if (newSpeciesName.trim()) {
      onError((await Session.getClient().request(SpeciesManagement.createSpecies({ name: newSpeciesName.trim() })))
        .maybeOk(() => searchSpecies(search)));
    }
    handleDialogClose();
  };


  const handleDeleteClick = (id: string, name: string) => {
    setSpeciesToDelete({ id, name });
    setDeleteDialogOpen(true);
  };

  const handleDeleteDialogClose = () => {
    setDeleteDialogOpen(false);
    setSpeciesToDelete(null);
  };

  const handleDeleteDialogOk = async () => {
    if (speciesToDelete) {
      onError((await Session.getSpeciesClient(speciesToDelete.id).request(SpeciesManagement.deleteSpecies()))
        .maybeOk(() => searchSpecies(search)));
    }
    handleDeleteDialogClose();
  };

  if (editingSpecies) {
    return <EditSpecies id={editingSpecies} onBack={() => {
      searchSpecies(search)
      setEditingSpecies(null)
    }} />;
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, gap: 2 }}>
        <TextField
          label="Search species"
          size="small"
          value={search}
          onChange={e => {
            setSearch(e.target.value)
            searchSpecies(e.target.value)
          }}
          sx={{ width: 250 }}
        />
        <AISuggestions 
          client={Session.getClient()}
          currentSnapshot={JSON.stringify(filteredSpecies)}
          usageInstructions={SpeciesManagement.toAgentInstructions()}
          developerMessage={'The user is in the view where they can search and see a list of species. For any of the operations ' +
            'regarding a specific species the path parameter with the name speciesId MUST be specified with that species id. For '+
            'breeds the path parameter breedId MUST also be specified with that breed id.'}
          onOperationsApplied={() => searchSpecies(search)}
        />
        <Button variant="contained" color="primary" onClick={handleAddSpecies}>Add species</Button>
      </Box>
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell><Typography fontWeight="bold">Name</Typography></TableCell>
              <TableCell><Typography fontWeight="bold">Number of breeds</Typography></TableCell>
              <TableCell><Typography fontWeight="bold">Published breeds</Typography></TableCell>
              <TableCell><Typography fontWeight="bold">Published</Typography></TableCell>
              <TableCell align='right'><Typography fontWeight="bold">Actions</Typography></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredSpecies.map((row) => {
              // For now, assume all breeds are published if species is published, else 0
              const publishedBreeds = row.publishedBreeds;
              return (
                <TableRow key={row.name}>
                  <TableCell>{row.name}</TableCell>
                  <TableCell>{row.numberOfBreeds}</TableCell>
                  <TableCell>{publishedBreeds}</TableCell>
                  <TableCell>
                    <Switch checked={row.state.state === 'published'} disabled />
                  </TableCell>
                  <TableCell align='right'>
                    <IconButton size="small" color="primary" onClick={() => setEditingSpecies(row.id)}>
                      <EditIcon />
                    </IconButton>
                    <IconButton size="small" color="error" onClick={() => handleDeleteClick(row.id, row.name)}>
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={dialogOpen} onClose={handleDialogClose}>
        <DialogTitle>Add Species</DialogTitle>
        <DialogContent>
          <TextField
            margin="dense"
            label="Species Name"
            fullWidth
            autoFocus
            value={newSpeciesName}
            onChange={e => setNewSpeciesName(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDialogClose}>Cancel</Button>
          <Button onClick={handleDialogOk} variant="contained">OK</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteDialogOpen} onClose={handleDeleteDialogClose}>
        <DialogTitle>Delete Species</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete the species "{speciesToDelete?.name}"?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteDialogClose}>Cancel</Button>
          <Button onClick={handleDeleteDialogOk} variant="contained" color="error">OK</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default SpeciesAndBreedsList;
